import { message } from 'antd'
import { ReactComponent as ErrorIcon } from 'assets/icons/cross.svg'
import { SuccessMessageTitle } from 'components/business/Main/Player/styled'
import dayjs from 'dayjs'
import {
  getShiurimFromFollowed,
  getShiurimFromFollowedVariables,
} from 'graphql/query/__generated__/getShiurimFromFollowed'
import {
  getShiurimOrderedByDate,
  getShiurimOrderedByDateVariables,
} from 'graphql/query/__generated__/getShiurimOrderedByDate'
import { GET_SHIURIM_FROM_FOLLOWED } from 'graphql/query/getShiurimFromFollowed'
import { GET_SHIURIM_ORDERED_BY_DATE } from 'graphql/query/getShiurimOrderedByDate'
import { usePlayer } from 'hooks/usePlayer'
import { QueryInfo } from 'hooks/useQueryInfo'

import { useLazyQuery } from '@apollo/client'

import { ShiurimAutoPlayInfo } from '../../Cards/ShiurimCard'
import { PlayIcon } from '../../Player/icons'
import { Button, Loader, Wrapper } from './styled'

type Props = {
  isPage?: boolean
  isSeries?: boolean
  isFollowing?: boolean
  date: dayjs.Dayjs
}

function PlayAllShiursByDateButton({ isPage, isSeries, isFollowing, date }: Props) {
  const { isInitialized, bulkAddToQueue } = usePlayer()

  const [getShiurimOrderedByDate, { loading: getShiurimOrderedByDateLoading }] = useLazyQuery<
    getShiurimOrderedByDate,
    getShiurimOrderedByDateVariables
  >(GET_SHIURIM_ORDERED_BY_DATE)

  const [getShiurimFromFollowed, { loading: getShiurimFromFollowedLoading }] = useLazyQuery<
    getShiurimFromFollowed,
    getShiurimFromFollowedVariables
  >(GET_SHIURIM_FROM_FOLLOWED)

  const showError = () => {
    message.config({
      maxCount: 1,
      top: isInitialized ? window.innerHeight - 170 : window.innerHeight - 100,
      getContainer: () => document.querySelector('#root'),
    })
    message.open({
      icon: <ErrorIcon />,
      content: <SuccessMessageTitle>There were no shiurim uploaded yet today</SuccessMessageTitle>,
    })
  }

  const onPlay = async () => {
    if (isSeries) {
      const variables = {
        searchParams: {
          startDate: date.toDate().toDateString(),
          endDate: date.add(1, 'day').toDate().toDateString(),
        },
        take: 50,
        page: 1,
      }
      const { data } = await getShiurimFromFollowed({
        variables,
      })
      const items = data.getShiurimFromFollowed.items
      const unplayedItems = items.filter((el) =>
        el.listenedDuration ? dayjs.duration(el.duration - el.listenedDuration).asSeconds() > 15 : true
      )
      const queueItems = [...unplayedItems]

      const shiurQueryInfo: QueryInfo = { query: GET_SHIURIM_FROM_FOLLOWED, variables }
      const itemsWithShiurimAutoPlayInfo = queueItems.map((el, index) => {
        const shiurAutoPlayInfo: ShiurimAutoPlayInfo = { ...shiurQueryInfo, index }
        return { ...el, shiurAutoPlayInfo }
      })

      if (itemsWithShiurimAutoPlayInfo.length) {
        bulkAddToQueue(itemsWithShiurimAutoPlayInfo, true)
      } else {
        showError()
      }

      return
    }

    if (isFollowing) {
      const variables = {
        searchParams: {
          startDate: date.toDate().toDateString(),
          endDate: date.add(1, 'day').toDate().toDateString(),
        },
        take: 50,
        page: 1,
      }
      const { data } = await getShiurimFromFollowed({
        variables,
      })
      const items = data.getShiurimFromFollowed.items
      const unplayedItems = items.filter((el) =>
        el.listenedDuration ? dayjs.duration(el.duration - el.listenedDuration).asSeconds() > 15 : true
      )
      const queueItems = [...unplayedItems]

      const shiurQueryInfo: QueryInfo = { query: GET_SHIURIM_FROM_FOLLOWED, variables }
      const itemsWithShiurimAutoPlayInfo = queueItems.map((el, index) => {
        const shiurAutoPlayInfo: ShiurimAutoPlayInfo = { ...shiurQueryInfo, index }
        return { ...el, shiurAutoPlayInfo }
      })

      if (itemsWithShiurimAutoPlayInfo.length) {
        bulkAddToQueue(itemsWithShiurimAutoPlayInfo, true)
      } else {
        showError()
      }
      return
    }

    const variables = { date: date.toDate().toDateString(), take: 50, page: 1 }
    const { data } = await getShiurimOrderedByDate({
      variables,
    })
    const items = data.getShiurimOrderedByDate.items
    const unplayedItems = items.filter((el) =>
      el.listenedDuration ? dayjs.duration(el.duration - el.listenedDuration).asSeconds() > 15 : true
    )

    const queueItems = [...unplayedItems]

    const shiurQueryInfo: QueryInfo = { query: GET_SHIURIM_ORDERED_BY_DATE, variables }
    const itemsWithShiurimAutoPlayInfo = queueItems.map((el, index) => {
      const shiurAutoPlayInfo: ShiurimAutoPlayInfo = { ...shiurQueryInfo, index }
      return { ...el, shiurAutoPlayInfo }
    })

    if (itemsWithShiurimAutoPlayInfo.length) {
      bulkAddToQueue(itemsWithShiurimAutoPlayInfo, true)
    } else {
      showError()
    }
  }

  const loading = getShiurimFromFollowedLoading || getShiurimOrderedByDateLoading
  return (
    <Wrapper isPage={isPage}>
      <Button type="primary" onClick={onPlay}>
        {isFollowing && !isPage ? "Play all today's" : 'Play all'}
        {loading ? <Loader size="small" /> : <PlayIcon />}
      </Button>
    </Wrapper>
  )
}

export default PlayAllShiursByDateButton
